<template>
  <div ref="progressBar" class="scroll-progress"></div>
  <div
    class="relative max-w-[110%] mx-auto text-center mx-[-20px] whitespace-pre-line"
  >
    <br />
    <h2
      id="CRM"
      data-rv
      class="rv-up responsive-title font-gilroy font-semibold uppercase text-black leading-tight tracking-[-1px] mx-auto"
    >
      <span class="font-glitch font-normal gradient-text"
        >{{ $t("Manage your practice professionally") }}
      </span>
      {{ $t("with fixed pricing and no surprises") }}
      <br />
    </h2>
    <p
      data-rv
      class="rv-up rv-d2 font-gilroy font-[500] text-[13px] leading-[20px] lg:text-[18px] lg:leading-[28px] text-black/50 mt-4 mx-auto px-2 max-w-[400px] md:max-w-[550px] lg:max-w-[706px]"
    >
      {{ $t("Health professionals software description") }}
    </p>
  </div>
  <br />
  <div class="grid grid-cols-1 gap-10 max-w-[700px] mx-auto px-9">
    <section v-for="s in sections" :key="s.title">
      <h3
        data-rv
        class="rv-up font-gilroy font-semibold text-[22px] leading-[28px] gradient-text text-center mb-8"
      >
        {{ $t(s.title) }}
      </h3>
      <div class="grid grid-cols-1 gap-8">
        <div
          v-for="(f, i) in s.items"
          :key="f.t"
          data-rv
          class="rv-item flex items-center gap-x-4 min-h-28 pb-4 border-b border-black/10 last:border-b-0"
          :class="i % 2 === 1 ? 'flex-row-reverse text-right rv-right' : 'rv-left'"
        >
          <div
            class="rv-dot w-[36px] h-[36px] rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-[#1AC0EA] to-[#0458E2]"
          ></div>
          <div>
            <h4
              class="font-gilroy font-semibold text-[18px] leading-[24px] text-black"
            >
              {{ $t(f.t) }}
            </h4>
            <p
              class="font-gilroy font-medium text-[14px] leading-[18px] text-black/70"
            >
              {{ $t(f.d) }}
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>

  <br />
  <br />

  <div class="flex flex-col gap-y-6 relative lg:min-h-[700px]">
    <div
      class="fixed bottom-6 right-6 lg:w-[278px] lg:h-[157px] w-[178px] h-[120px] bg-[#000519] backdrop-blur-lg border border-white/10 rounded-[24px] p-6 text-left shadow-lg z-[9999]"
    >
      <div class="relative z-10 flex-col justify-between h-full">
        <h3
          class="font-gilroy font-medium text-[14px] leading-none text-white p-1"
        >
          {{ $t("Request a demo") }}
        </h3>
        <p
          class="font-gilroy font-normal text-[12px] leading-[18px] text-white/70 mt-2 max-w-[177px]"
        >
          {{ $t("and start transforming your practice today") }}
        </p>
        <p class="mt-3">
          <a
            href="https://wa.me/+34649890875"
            target="_blank"
            class="inline-flex items-center justify-center lg:mt-[8px] lg:w-[246px] lg:h-[48px] w-[144px] h-[24px] rounded-full text-white bg-gradient-to-br from-[#1AC0EA] to-[#0458E2] hover:shadow-lg hover:shadow-blue-500/40 transition-shadow"
            ><span class="font-urbanist font-semibold text-[16px] leading-6">{{
              $t("Contact")
            }}</span></a
          >
        </p>
      </div>
    </div>
  </div>

  <FooterMenuSection />
</template>

<script>
import { defineComponent, onMounted, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import FooterMenuSection from "@/components/home/sections/footerMenu/footerMenuSection.vue";
import { useSeo } from "@/composables/useSeo";

const sections = [
  {
    title: "Bookings and smart scheduling",
    items: [
      {
        t: "Real-time booking engine",
        d: "A single calendar combining professionals, rooms, equipment, capacities and preparation and cleaning times, so every channel checks the same real-time availability",
      },
      {
        t: "Online booking 24/7 on every channel",
        d: "Booking website, patient portal, reception desk and the Medkus.com marketplace, all working on the same availability",
      },
      {
        t: "Waiting list and automatic gap filling",
        d: "Cancellations refill themselves: waiting list with notifications, one-click confirmation and expiry, connected to automatic gap filling",
      },
      {
        t: "Automatic reminders and confirmations",
        d: "Appointment reminders, confirmations and document-signature requests by email, SMS and WhatsApp, with every message logged in the CRM",
      },
      {
        t: "Deposits and no-show protection",
        d: "Online deposits with configurable retention when the patient does not show up, and automatic refunds when the cancellation allows it",
      },
      {
        t: "Check-in and resource management",
        d: "Day view for reception with check-in, and rooms, equipment and lockers managed as bookable resources with their own availability",
      },
    ],
  },
  {
    title: "Clinical record and patient CRM",
    items: [
      {
        t: "Single 360º patient record",
        d: "One record per patient, with identification by email, phone or ID document and duplicate merging: bookings, payments, documents, communications and clinical history on one screen",
      },
      {
        t: "Encrypted clinical history",
        d: "Chronological clinical record with health data encrypted in the database, role-based access and a complete audit trail",
      },
      {
        t: "AI that reads lab results",
        d: "Upload a lab report or medical result: the AI interprets it and turns it into structured clinical data inside the patient's history",
      },
      {
        t: "Digital consents and questionnaires",
        d: "Informed consents, GDPR forms and health questionnaires signed from the patient portal, with versioning",
      },
      {
        t: "Treatment plans in minutes",
        d: "Build the patient's plan in a couple of clicks and send it as a PDF with your branding",
      },
      {
        t: "Post-visit follow-up with churn alerts",
        d: "Continuous follow-up between visits: inactivity detection, abandonment-risk alerts and automatic recovery campaigns",
      },
    ],
  },
  {
    title: "Sales and revenue",
    items: [
      {
        t: "Catalogue of experiences, programmes and packs",
        d: "Services, composite experiences, therapeutic programmes with planned sessions and commercial packs, sold on every channel from one catalogue",
      },
      {
        t: "Vouchers and gift cards",
        d: "Session or amount vouchers with validity, usage limits and partial redemption, plus gift cards",
      },
      {
        t: "Memberships with recurring billing",
        d: "Recurring fees with automatic renewal and control of sign-ups, cancellations and suspensions",
      },
      {
        t: "Verifactu-compliant invoicing",
        d: "Invoices, simplified and corrective invoices, cash register and till reconciliation, compliant with the Spanish Verifactu regulation",
      },
      {
        t: "Payment gateways and online store",
        d: "Stripe, Redsys and PayPal, cash and internal wallet, plus an integrated online store and sales to companies, hotels and clubs",
      },
      {
        t: "Expense tracking with AI and profitability",
        d: "Take a photo of a ticket or invoice: the AI extracts amount, supplier and category, and the profitability report crosses income against expenses",
      },
    ],
  },
  {
    title: "Communication and marketing",
    items: [
      {
        t: "Email, SMS and WhatsApp in one place",
        d: "Every message sent and received is logged in the patient's record: a single unified communication history",
      },
      {
        t: "Lifecycle automations",
        d: "Welcome flows, reminders, confirmations, post-visit thanks, review requests, birthdays and win-back campaigns for inactive patients, configured without coding",
      },
      {
        t: "Patient portal",
        d: "Bookings, documents to sign, payments and history in a private area: responsive web with no app to download",
      },
      { t: "Website, online store, blog", d: "web_and_more" },
      {
        t: "Profile on the Medkus.com marketplace",
        d: "Public profile with reviews and online booking on the Medkus marketplace: one more channel to attract new patients",
      },
      { t: "Events and Classes", d: "classes_more" },
      {
        t: "Sell your courses and certifications",
        d: "Academy module: masters, courses and certifications sold online, with syllabus, student progress and certificates",
      },
    ],
  },
  {
    title: "Business control and reporting",
    items: [
      {
        t: "Dashboards and reports by area",
        d: "Management, sales, operations, finance and patient experience, reading live operational data with no manual consolidation",
      },
      {
        t: "Occupancy and activity reports",
        d: "Occupancy by resource and time slot, cancellations, no-shows and team utilisation",
      },
      { t: "Roles and permissions", d: "roles_more" },
      {
        t: "Multi-location ready",
        d: "One platform for several locations: a single patient record across the network, with calendars, staff and inventory per location",
      },
    ],
  },
  {
    title: "Security, your data and integrations",
    items: [
      {
        t: "Healthcare-grade GDPR compliance",
        d: "Built for health data: encryption at the database level, per-request traceability with user, and compliance documentation available for due diligence",
      },
      {
        t: "European infrastructure",
        d: "Servers in the EU (OVH) with real-time replication to a second datacenter and additional backups in Spain, so your data never leaves the European Union",
      },
      {
        t: "Your data is yours",
        d: "Full structured export of your database and documents, critical accounts contracted in your name, and no minimum term: zero lock-in",
      },
      {
        t: "Documented REST API",
        d: "REST API with JWT authentication and a sandbox environment for integrations with your lab, ERP or BI tool",
      },
      {
        t: "Multi-language platform",
        d: "Spanish, English, French and Russian today; adding a new language requires no redesign",
      },
      {
        t: "ISO 27001 certification in progress",
        d: "Information-security certification under way, with security hardening, including two-factor authentication, in the same package",
      },
    ],
  },
  {
    title: "Coming soon: AI across the whole platform",
    items: [
      {
        t: "AI advisor in the patient portal",
        d: "Recommends experiences and programmes based on history, questionnaires and real availability",
      },
      {
        t: "Conversational bookings on WhatsApp",
        d: "Find a slot, book, charge and confirm inside the conversation, on the same real-time availability",
      },
      {
        t: "Natural-language analytics",
        d: "Ask what was the best-selling experience this month and get the answer with tables and charts",
      },
      {
        t: "Smart voice surveys",
        d: "Patients answer surveys by speaking from their phone; the AI transcribes the answer and analyses their mood",
      },
    ],
  },
];

export default defineComponent({
  name: "ForProfessionalsFunctionality",
  components: {
    FooterMenuSection,
  },
  setup() {
    const { t } = useI18n();
    useSeo({ title: t("seoForProFuncTitle"), description: t("seoForProFuncDesc") });

    let observer = null;
    const progressBar = ref(null);
    let ticking = false;

    const updateProgress = () => {
      ticking = false;
      const el = progressBar.value;
      if (!el) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      el.style.transform = `scaleX(${p})`;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateProgress);
      }
    };

    onMounted(() => {
      document
        .querySelector(".back-to-top")
        ?.addEventListener("click", function (event) {
          event.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        });

      window.addEventListener("scroll", onScroll, { passive: true });
      updateProgress();

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        document
          .querySelectorAll("[data-rv]")
          .forEach((el) => el.classList.add("in"));
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("in");
              observer.unobserve(e.target);
            }
          });
        },
        { threshold: 0.15 },
      );
      document
        .querySelectorAll("[data-rv]")
        .forEach((el) => observer.observe(el));
    });

    onBeforeUnmount(() => {
      observer && observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    });

    return { sections, progressBar };
  },
});
</script>

<style scoped>
.scroll-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 10000;
  background: linear-gradient(90deg, #1ac0ea, #0458e2);
  transform: scaleX(0);
  transform-origin: 0 50%;
  pointer-events: none;
}

[data-rv] {
  opacity: 0;
  transition:
    opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}
[data-rv].rv-up {
  transform: translateY(28px);
}
[data-rv].rv-d2 {
  transition-delay: 0.15s;
}
[data-rv].rv-left {
  transform: translateX(-40px) scale(0.96);
}
[data-rv].rv-right {
  transform: translateX(40px) scale(0.96);
}
[data-rv].in {
  opacity: 1;
  transform: none;
}

.rv-item:nth-child(2) {
  transition-delay: 0.08s;
}
.rv-item:nth-child(3) {
  transition-delay: 0.16s;
}
.rv-item:nth-child(4) {
  transition-delay: 0.24s;
}
.rv-item:nth-child(5) {
  transition-delay: 0.32s;
}
.rv-item:nth-child(6) {
  transition-delay: 0.4s;
}
.rv-item:nth-child(7) {
  transition-delay: 0.48s;
}

.rv-dot {
  opacity: 0;
  transform: scale(0.3);
  transition:
    transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s,
    opacity 0.35s ease 0.2s,
    box-shadow 0.3s ease;
}
.in .rv-dot {
  opacity: 1;
  transform: scale(1);
}
.rv-item:hover .rv-dot {
  box-shadow: 0 0 0 6px rgb(26 192 234 / 0.18);
}
.rv-item {
  transition-property: opacity, transform, border-color;
}
.rv-item:hover {
  border-color: rgb(4 88 226 / 0.35);
}
</style>
